// Card costs (/supplier-orders): every single bought from Cardrush and Hareruya, what we paid for it
// in pounds, and CardCam's recorded ACE sales for the same card. Owners also get the order notices
// and import checks as a second tab.
//
// Layout: a search and supplier bar, a Filters panel (inline on desktop, a bottom sheet on phones),
// removable chips for the filters in use, then a grid of cards. Opening a card shows its purchase
// details in a column beside the grid on wide screens and in a bottom sheet below 1100px.
// Search, filters, sort and page live in the address bar, so back, refresh and shared links keep them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretLeft, CaretRight, ImageBroken, MagnifyingGlass, SlidersHorizontal, X } from '@phosphor-icons/react';
import { Alert, Badge, Button, EmptyState, Page, PageHeader, Skeleton, SkeletonRows } from 'design';
import { getStoredUser } from '../../utils/auth';
import { isInventoryOwner, canViewSupplierCosts } from '../../utils/inventoryNavigation.mjs';
import { createSupplierLibraryApi } from './supplierLibraryApi.mjs';
import { createSupplierOrdersApi } from './supplierOrdersApi.mjs';
import { SupplierOrdersReview as SupplierOrderMessages } from './SupplierOrderMessages';
import './SupplierOrders.css';
import SupplierRecordedSales from './SupplierRecordedSales';
import SupplierPerformance from './SupplierPerformance';

const PAGE_SIZE = 30;
const SUPPLIERS = [{ value: '', label: 'All' }, { value: 'cardrush', label: 'Cardrush' }, { value: 'hareruya', label: 'Hareruya' }];
const FINISHES = [
  { value: '', label: 'Any finish' },
  { value: 'pokeball', label: 'Poké Ball mirror' },
  { value: 'masterball', label: 'Master Ball mirror' },
  { value: 'other_mirror', label: 'Other mirror or reverse' },
  { value: 'unspecified', label: 'No mirror specified' },
];
// The price each card shows, filters on and sorts by.
const BASES = [
  { value: 'latest', label: 'Latest paid', short: 'Latest', field: 'latest_gbp' },
  { value: 'average30', label: '30 day average', short: '30 days', field: 'average_30_gbp' },
  { value: 'average', label: 'All time average', short: 'All time', field: 'average_gbp' },
];
const SORTS = [
  { value: 'recent', label: 'Recently purchased' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'name', label: 'Card name' },
  { value: 'quantity', label: 'Most purchased' },
];
const PRICE = /^\d{1,7}(?:\.\d{1,2})?$/;
const YEAR = /^(19|20)\d\d$/;
const NOTE = 'GBP is converted from yen and excludes shipping and card fees. Averages are weighted by quantity and use available converted costs. Orders with unresolved change or cancellation notices are excluded until reviewed. The all time average covers imported orders only; the older Cardrush archive has not yet been reconciled. The same supplier\'s sale and condition descriptions are grouped; each purchase retains its condition. Different suppliers, finishes and sets remain separate.';

const pounds = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const supplierName = value => value === 'cardrush' ? 'Cardrush' : 'Hareruya';
const money = value => value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : pounds.format(Number(value));
const yen = value => value == null || !Number.isFinite(Number(value)) ? '—' : `¥${Number(value).toLocaleString('en-GB')}`;
const count = value => Number(value || 0).toLocaleString('en-GB');
const condition = value => value?.match(/〔状態([^〕]+)〕/u)?.[1] || null;
const displayDate = value => typeof value === 'string' && /^\d{4}-\d\d-\d\d/.test(value) ? new Date(`${value.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Date unavailable';
const cardKey = card => `${card.supplier}-${card.id}`;
const setLine = card => [card.catalogue_set_name, card.collector_number].filter(Boolean).join(' · ');
const validPrice = value => value === '' || (PRICE.test(value) && Number(value) <= 1000000);
const priceProblem = (min, max) => !validPrice(min) || !validPrice(max) ? 'Enter prices in pounds, like 12.50.'
  : min !== '' && max !== '' && Number(min) > Number(max) ? 'Minimum price must be less than maximum price.' : '';
const basisFor = value => BASES.find(item => item.value === value) || BASES[0];

// ── Address bar state ────────────────────────────────────────────────────
function readAddress() {
  const state = {};
  try {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('q');
    if (search && search.length <= 120) state.search = search;
    if (['cardrush', 'hareruya'].includes(params.get('supplier'))) state.supplier = params.get('supplier');
    if (/^[a-z0-9-]{1,30}$/i.test(params.get('set') || '')) state.setCode = params.get('set');
    if (YEAR.test(params.get('year') || '')) state.year = params.get('year');
    if (FINISHES.some(item => item.value && item.value === params.get('finish'))) state.finish = params.get('finish');
    const min = params.get('min') || '', max = params.get('max') || '';
    if (!priceProblem(min, max)) { state.minPrice = min; state.maxPrice = max; }
    if (BASES.some(item => item.value === params.get('price'))) state.priceBasis = params.get('price');
    if (SORTS.some(item => item.value === params.get('sort'))) state.sort = params.get('sort');
    const page = Number(params.get('page'));
    if (Number.isSafeInteger(page) && page > 1 && page <= 30000) state.offset = (page - 1) * PAGE_SIZE;
    if (['notices','performance'].includes(params.get('view'))) state.view = params.get('view');
  } catch (_) { /* no address bar state */ }
  return state;
}

function writeAddress(values) {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value, fallback = ''] of values) {
      if (value && value !== fallback) params.set(key, value); else params.delete(key);
    }
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) window.history.replaceState(window.history.state, '', next);
  } catch (_) { /* the address bar is a convenience */ }
}

// ── Artwork ──────────────────────────────────────────────────────────────
function Artwork({ card, large = false }) {
  const main = large ? card.image_full_url || card.image_url : card.image_url;
  const [source, setSource] = useState(main || null);
  useEffect(() => setSource(main || null), [main]);
  if (!source) {
    return <span className={`sc-art sc-art--empty${large ? ' sc-art--large' : ''}`}>
      <ImageBroken size={24} aria-hidden="true" />
      <small>{/mirror|reverse/i.test(card.variant_label || '') ? 'Finish image unavailable' : 'No image yet'}</small>
    </span>;
  }
  return <span className={`sc-art${large ? ' sc-art--large' : ''}`}>
    <img src={source} alt={`${card.image_is_base ? 'Base artwork' : 'Suggested card artwork'} for ${card.catalogue_name || card.raw_card_name}`} width="150" height="210" loading={large ? 'eager' : 'lazy'} decoding="async" referrerPolicy="no-referrer" onError={() => setSource(previous => previous !== card.image_fallback_url ? card.image_fallback_url || null : null)} />
    {card.image_is_base && <span className="sc-art__flag">Base artwork</span>}
  </span>;
}

function ArtworkZoom({ card, onClose }) {
  const [source, setSource] = useState(card.image_full_url || card.image_url);
  const close = useRef(null);
  useEffect(() => {
    close.current?.focus({ preventScroll: true });
    const onKey = event => { if (event.key === 'Escape') { event.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return <div className="rc-backdrop sc-zoom" role="dialog" aria-modal="true" aria-label={`Artwork for ${card.catalogue_name || card.raw_card_name}`} onClick={onClose}>
    {source && <img src={source} alt={`Artwork for ${card.catalogue_name || card.raw_card_name}`} referrerPolicy="no-referrer" onError={() => setSource(previous => previous !== card.image_fallback_url ? card.image_fallback_url || null : null)} />}
    <button ref={close} type="button" className="rc-btn rc-btn--icon sc-zoom__close" onClick={onClose} aria-label="Close artwork"><X size={20} aria-hidden="true" /></button>
  </div>;
}

// ── One card in the grid ─────────────────────────────────────────────────
function CardTile({ card, basis, selected, onOpen }) {
  const value = card[basis.field];
  const bought = basis.value === 'average30' ? card.quantity_30 : card.quantity;
  return <button type="button" className={`sc-card rc-lit${selected ? ' sc-card--selected' : ''}`} aria-current={selected ? 'true' : undefined} data-card-key={cardKey(card)} onClick={() => onOpen(card)}>
    <span className="sc-card__stage"><Artwork card={card} /></span>
    <span className="sc-card__body">
      <span className="sc-card__meta"><span className="rc-badge">{supplierName(card.supplier)}</span></span>
      <span className="sc-card__name">{card.catalogue_name || card.raw_card_name}</span>
      {setLine(card) && <span className="sc-card__set">{setLine(card)}</span>}
      {card.variant_label && <span className="sc-card__finish">{card.variant_label}</span>}
      <span className="sc-card__price">
        <span className="sc-card__amount">{money(value)}</span>
        <span className="sc-card__qty">{basis.value === 'average30' ? (bought ? `${count(bought)} in 30 days` : 'None in 30 days') : `${count(bought)} bought`}</span>
      </span>
      {basis.value === 'latest' && card.latest_needs_review ? <span className="sc-card__basis sc-warn">Latest order needs review</span>
        : <span className="sc-card__basis">{basis.value === 'latest' ? (card.latest_date ? `Last paid · ${displayDate(card.latest_date)}` : 'Last paid') : basis.label}</span>}
    </span>
  </button>;
}

function TileSkeletons() {
  return <div className="sc-grid" role="status" aria-label="Loading cards">
    {Array.from({ length: 14 }, (_, index) => <div className="sc-ghost" key={index}>
      <span className="sc-ghost__stage"><Skeleton width="62%" height="82%" radius={8} /></span>
      <span className="sc-ghost__body"><Skeleton width="78%" height={14} /><Skeleton width="56%" height={12} /><Skeleton width="44%" height={24} /></span>
    </div>)}
  </div>;
}

// ── Searchable set filter ────────────────────────────────────────────────
function SetPicker({ sets, year, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const list = useRef(null);
  const input = useRef(null);
  const chosen = sets.find(item => item.code === value);
  const label = item => `${item.name} (${item.code.toUpperCase()})`;
  const options = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return sets.filter(item => (!year || item.year === year) && words.every(word => `${item.name} ${item.code}`.toLowerCase().includes(word)))
      .sort((a, b) => String(b.year || '').localeCompare(String(a.year || '')) || a.name.localeCompare(b.name));
  }, [sets, year, query]);
  useEffect(() => { setActive(0); }, [query, open]);
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);
  function choose(item) { onChange(item ? item.code : ''); setQuery(''); setOpen(false); input.current?.blur(); }
  function onKeyDown(event) {
    if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setActive(index => Math.min(options.length - 1, index + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(index => Math.max(0, index - 1)); }
    else if (event.key === 'Enter' && open) { event.preventDefault(); if (options[active]) choose(options[active]); }
    else if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false); setQuery(''); }
  }
  let lastYear = null;
  return <div className="rc-field sc-setpicker">
    <label className="rc-label" htmlFor="sc-set-input">Set</label>
    <span className="rc-input">
      <MagnifyingGlass size={16} aria-hidden="true" />
      <input ref={input} id="sc-set-input" role="combobox" aria-expanded={open} aria-controls="supplier-set-options" aria-autocomplete="list" aria-activedescendant={open && options[active] ? `sc-set-${options[active].code}` : undefined}
        aria-label="Search sets" autoComplete="off" spellCheck={false} placeholder={chosen ? label(chosen) : 'All sets'}
        value={open ? query : chosen ? label(chosen) : value ? value.toUpperCase() : ''}
        onFocus={() => setOpen(true)} onBlur={() => { setOpen(false); setQuery(''); }}
        onChange={event => { setQuery(event.target.value); setOpen(true); }} onKeyDown={onKeyDown} />
      {value && <button type="button" className="sc-input-clear" aria-label="Clear set" onMouseDown={event => event.preventDefault()} onClick={() => choose(null)}><X size={14} aria-hidden="true" /></button>}
    </span>
    <ul id="supplier-set-options" ref={list} className="sc-setpicker__list" role="listbox" aria-label="Sets" hidden={!open}>
      {options.length === 0 && <li className="sc-setpicker__none" role="presentation">{sets.length ? 'No sets match' : 'Sets are loading'}</li>}
      {options.map((item, index) => {
        const heading = item.year !== lastYear ? <li key={`year-${item.year}`} className="sc-setpicker__year rc-label" role="presentation">{item.year || 'Year unknown'}</li> : null;
        lastYear = item.year;
        return <React.Fragment key={item.code}>{heading}<li id={`sc-set-${item.code}`} data-index={index} role="option" aria-selected={item.code === value} className={index === active ? 'is-active' : undefined}
          onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(item)}>
          <span>{item.name}</span><span className="rc-mono">{item.code.toUpperCase()}</span>
        </li></React.Fragment>;
      })}
    </ul>
  </div>;
}

// ── Purchase details ─────────────────────────────────────────────────────
function Figure({ label, value, note, active }) {
  return <div className={`sc-figure${active ? ' sc-figure--active' : ''}`}>
    <span className="sc-figure__label">{label}</span>
    <strong className="sc-figure__value">{value}</strong>
    <span className="sc-figure__note">{note}</span>
  </div>;
}

function Detail({ card, api, basis, position, total, onPrevious, onNext, onClose }) {
  const panel = useRef(null);
  const scroller = useRef(null);
  const [state, setState] = useState({ loading: true, history: [], total: 0, error: '' });
  const [zoom, setZoom] = useState(false);
  const closeZoom = useCallback(() => setZoom(false), []);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = 0; setZoom(false); }, [card.id]);
  useEffect(() => {
    // As a bottom sheet the details take focus; beside the grid they leave it on the card.
    if (window.matchMedia?.('(max-width: 1099px)').matches) panel.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    // Beside the grid the column ends at the bottom of the screen, so all of it can be read
    // before the page has scrolled far enough for it to stick under the top bar.
    const wide = window.matchMedia?.('(min-width: 1100px)');
    const node = panel.current;
    if (!wide || !node) return undefined;
    let frame = 0;
    const fit = () => {
      frame = 0;
      node.style.maxHeight = wide.matches ? `${Math.max(320, Math.floor(window.innerHeight - Math.max(node.getBoundingClientRect().top, 0) - 16))}px` : '';
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(fit); };
    fit();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); if (frame) window.cancelAnimationFrame(frame); node.style.maxHeight = ''; };
  }, []);
  useEffect(() => {
    let active = true;
    setState({ loading: true, history: [], total: 0, error: '' });
    api.history(card.id).then(data => { if (active) setState({ ...data, loading: false, error: '' }); })
      .catch(error => { if (active) setState({ loading: false, history: [], total: 0, error: error.message }); });
    return () => { active = false; };
  }, [api, card.id]);
  const partial = Number(card.priced_quantity || 0) < Number(card.quantity || 0);
  const partial30 = Number(card.priced_quantity_30 || 0) < Number(card.quantity_30 || 0);
  const name = card.catalogue_name || card.raw_card_name;
  const hasImage = Boolean(card.image_full_url || card.image_url);
  return <>
    <div className="sc-detail-scrim" onClick={onClose} aria-hidden="true" />
    <aside ref={panel} className="sc-detail" aria-label="Card purchase history" tabIndex={-1}>
      <div className="sc-detail__bar">
        <span className="sc-detail__position">{position} of {total}</span>
        <div className="sc-detail__actions">
          <button type="button" className="rc-btn rc-btn--ghost rc-btn--icon" onClick={onPrevious} disabled={!onPrevious} aria-label="Previous card"><CaretLeft size={18} aria-hidden="true" /></button>
          <button type="button" className="rc-btn rc-btn--ghost rc-btn--icon" onClick={onNext} disabled={!onNext} aria-label="Next card"><CaretRight size={18} aria-hidden="true" /></button>
          <button type="button" className="rc-btn rc-btn--ghost rc-btn--icon" onClick={onClose} aria-label="Close purchase details"><X size={18} aria-hidden="true" /></button>
        </div>
      </div>
      <div className="sc-detail__scroll" ref={scroller}>
        <div className="sc-detail__hero">
          <button type="button" className="sc-detail__art" onClick={() => setZoom(true)} disabled={!hasImage} aria-label={`View larger artwork for ${name}`}>
            <Artwork key={cardKey(card)} card={card} large />
          </button>
          <div className="sc-detail__title">
            <div className="sc-detail__tags"><Badge>{supplierName(card.supplier)}</Badge>{card.variant_label && <Badge>{card.variant_label}</Badge>}</div>
            <h2>{name}</h2>
            {setLine(card) && <p className="sc-detail__set">{setLine(card)}</p>}
            {card.catalogue_name && <p className="sc-detail__raw" lang="ja">{card.raw_card_name}</p>}
            <p className="sc-detail__meta">Last purchased {displayDate(card.latest_date)}</p>
            {card.image_url && <p className="sc-detail__meta">{card.image_is_base ? 'Base artwork · Poké Ball mirror' : 'Catalogue image · suggested match'}</p>}
          </div>
        </div>

        <div className="sc-figures">
          <Figure label="Latest paid" value={money(card.latest_gbp)} active={basis.value === 'latest'}
            note={card.latest_needs_review ? <span className="sc-warn">Order change needs review</span> : `${yen(card.latest_jpy)} per card`} />
          <Figure label="30 day average" value={money(card.average_30_gbp)} active={basis.value === 'average30'}
            note={card.quantity_30 ? `${count(card.quantity_30)} cards${partial30 ? ' · some costs need review' : ''}` : 'No purchases'} />
          <Figure label="All time average" value={money(card.average_gbp)} active={basis.value === 'average'}
            note={`${count(card.quantity)} cards${partial ? ' · some costs need review' : ''}`} />
        </div>
        <p className="sc-detail__fine">Converted from yen. Excludes shipping and card fees.</p>

        <section className="sc-detail__section" aria-labelledby="sc-history-title">
          <div className="sc-detail__head"><h3 id="sc-history-title">Price history</h3>{!state.loading && !state.error && <span>{count(state.total)} {state.total === 1 ? 'purchase' : 'purchases'}</span>}</div>
          {state.loading ? <SkeletonRows rows={3} label="Loading purchase history" />
            : state.error ? <Alert tone="error">{state.error}</Alert>
            : state.history.length === 0 ? <p className="sc-detail__fine">No purchases are recorded for this card yet.</p>
            : <div className="rc-table-wrap"><table className="rc-table sc-table">
              <thead><tr><th scope="col">Date</th><th scope="col">Order</th><th scope="col" className="rc-num">Qty</th><th scope="col" className="rc-num">Each</th></tr></thead>
              <tbody>{state.history.map(row => <tr key={row.id}>
                <td>{displayDate(row.order_date)}{row.date_basis === 'email_received_date' && <small>Email date</small>}</td>
                <td><span className="rc-mono">#{row.supplier_order_id}</span>{condition(row.raw_card_name) && <small>Condition {condition(row.raw_card_name)}</small>}</td>
                <td className="rc-num">{row.quantity}{row.removed_quantity > 0 && <small>{row.removed_quantity} removed by supplier</small>}</td>
                <td className="rc-num"><strong>{money(row.unit_gbp)}</strong>{row.needs_review ? <small className="sc-warn">Order change needs review</small> : row.removed_quantity > 0 ? <small>{yen(row.refund_expected_jpy)} refund advised</small> : <small>{yen(row.unit_price_jpy)}</small>}</td>
              </tr>)}</tbody>
            </table></div>}
          {!state.loading && !state.error && state.total > state.history.length && <p className="sc-detail__fine">Showing the most recent {state.history.length} purchases.</p>}
        </section>

        <SupplierRecordedSales key={cardKey(card)} card={card} api={api} />

        <details className="rc-disclosure sc-detail__note">
          <summary>How these prices are worked out</summary>
          <div className="rc-disclosure__body"><p className="sc-note">{NOTE}</p></div>
        </details>
      </div>
    </aside>
    {zoom && <ArtworkZoom card={card} onClose={closeZoom} />}
  </>;
}

// ── The page ─────────────────────────────────────────────────────────────
export function SupplierOrdersReview({ api, messagesApi }) {
  const initial = useMemo(readAddress, []);
  const [view, setView] = useState(api.performance&&initial.view==='performance'?'performance':messagesApi && initial.view === 'notices' ? 'notices' : 'cards');
  const [search, setSearch] = useState(initial.search || ''), [debounced, setDebounced] = useState(initial.search || '');
  const [supplier, setSupplier] = useState(initial.supplier || '');
  const [setCode, setSetCode] = useState(initial.setCode || ''), [year, setYear] = useState(initial.year || ''), [facets, setFacets] = useState([]);
  const [finish, setFinish] = useState(initial.finish || '');
  const [minPrice, setMinPrice] = useState(initial.minPrice || ''), [maxPrice, setMaxPrice] = useState(initial.maxPrice || '');
  const [priceRange, setPriceRange] = useState({ minPrice: initial.minPrice || '', maxPrice: initial.maxPrice || '' });
  const [priceBasis, setPriceBasis] = useState(initial.priceBasis || 'latest'), [sort, setSort] = useState(initial.sort || 'recent');
  const [offset, setOffset] = useState(initial.offset || 0), [reload, setReload] = useState(0);
  const [cards, setCards] = useState([]), [total, setTotal] = useState(0), [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [selectedKey, setSelectedKey] = useState(null), [filtersOpen, setFiltersOpen] = useState(false);
  const searchInput = useRef(null), results = useRef(null), generation = useRef(0), pendingSelect = useRef(null);
  const basis = basisFor(priceBasis);

  // Typing a release year or an exact set name in the search box filters by it.
  const yearSearch = YEAR.test(debounced) ? debounced : '';
  const exactSetSearch = useMemo(() => {
    if (!debounced) return '';
    if (setCode) return facets.some(item => item.code === setCode && item.name.toLowerCase() === debounced.toLowerCase()) ? setCode : '';
    const matches = facets.filter(item => item.name.toLowerCase() === debounced.toLowerCase());
    return matches.length === 1 ? matches[0].code : '';
  }, [debounced, setCode, facets]);
  // The price basis only changes the server's answer when it filters or sorts by price.
  const basisApplies = priceRange.minPrice !== '' || priceRange.maxPrice !== '' || sort.startsWith('price_');
  const listBasis = basisApplies ? priceBasis : 'latest';

  useEffect(() => { let active = true; if (api.facets) api.facets().then(data => { if (active) setFacets(data); }).catch(() => {}); return () => { active = false; }; }, [api]);
  useEffect(() => {
    if (search.trim() === debounced) return undefined;
    const timer = setTimeout(() => { setOffset(0); setDebounced(search.trim()); }, 250);
    return () => clearTimeout(timer);
  }, [search, debounced]);
  const priceError = priceProblem(minPrice, maxPrice);
  useEffect(() => {
    if (minPrice === priceRange.minPrice && maxPrice === priceRange.maxPrice) return undefined;
    const timer = setTimeout(() => { if (!priceProblem(minPrice, maxPrice)) { setOffset(0); setPriceRange({ minPrice, maxPrice }); } }, 400);
    return () => clearTimeout(timer);
  }, [minPrice, maxPrice, priceRange]);
  useEffect(() => {
    if(view!=='cards')return undefined;
    const current = ++generation.current;
    setLoading(true); setError('');
    api.list({ search: yearSearch || exactSetSearch ? '' : debounced, supplier, set: setCode || exactSetSearch, year: yearSearch || year, offset, priceBasis: listBasis, ...priceRange, finish, sort })
      .then(data => {
        if (generation.current !== current) return;
        // Stepping past either end of a page opens the next page at its first card, or the previous at its last.
        const pending = pendingSelect.current && pendingSelect.current.offset === data.offset ? pendingSelect.current : null;
        if (pending) pendingSelect.current = null;
        const jump = pending ? data.cards[pending.which === 'first' ? 0 : data.cards.length - 1] : null;
        setCards(data.cards); setTotal(data.total);
        setSelectedKey(key => jump ? cardKey(jump) : key && data.cards.some(card => cardKey(card) === key) ? key : null);
      })
      .catch(err => { if (generation.current !== current) return; setError(err?.message || 'Could not load cards. Please try again.'); setCards([]); setTotal(0); setSelectedKey(null); })
      .finally(() => { if (generation.current === current) setLoading(false); });
    return () => { generation.current++; };
  }, [api, debounced, supplier, setCode, exactSetSearch, yearSearch, year, offset, listBasis, priceRange, finish, sort, reload, view]);
  useEffect(() => {
    writeAddress([['q', debounced], ['supplier', supplier], ['set', setCode], ['year', year], ['finish', finish], ['min', priceRange.minPrice], ['max', priceRange.maxPrice],
      ['price', priceBasis, 'latest'], ['sort', sort, 'recent'], ['page', offset ? String(Math.floor(offset / PAGE_SIZE) + 1) : ''], ['view', view === 'cards' ? '' : view]]);
  }, [debounced, supplier, setCode, year, finish, priceRange, priceBasis, sort, offset, view]);

  const selectedIndex = selectedKey ? cards.findIndex(card => cardKey(card) === selectedKey) : -1;
  const selected = selectedIndex >= 0 ? cards[selectedIndex] : null;
  const closeDetail = useCallback(() => {
    const key = selectedKey;
    setSelectedKey(null);
    if (key) window.requestAnimationFrame?.(() => document.querySelector(`[data-card-key="${key}"]`)?.focus({ preventScroll: true }));
  }, [selectedKey]);
  const step = useCallback(direction => {
    if (selectedIndex < 0 || loading) return;
    const next = cards[selectedIndex + direction];
    if (next) { setSelectedKey(cardKey(next)); return; }
    const target = offset + direction * PAGE_SIZE;
    if (target < 0 || target >= total) return;
    pendingSelect.current = { offset: target, which: direction > 0 ? 'first' : 'last' };
    setOffset(target);
  }, [cards, selectedIndex, loading, offset, total]);
  // Keep the open card in view as the details move through the cards.
  useEffect(() => {
    if (selectedKey) document.querySelector(`[data-card-key="${selectedKey}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedKey]);

  // Sheets stop the page scrolling behind them; the CSS decides when they are sheets.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('sc-lock-filters', filtersOpen && view === 'cards');
    root.classList.toggle('sc-lock-detail', Boolean(selected) && view === 'cards');
    return () => root.classList.remove('sc-lock-filters', 'sc-lock-detail');
  }, [filtersOpen, selected, view]);

  useEffect(() => {
    if (view !== 'cards') return undefined;
    const onKey = event => {
      if (event.defaultPrevented) return;
      const target = event.target;
      const typing = target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      if (event.key === 'Escape') {
        if (selected) closeDetail(); else if (filtersOpen) setFiltersOpen(false);
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '/') { event.preventDefault(); searchInput.current?.focus(); }
      else if (selected && event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
      else if (selected && event.key === 'ArrowRight') { event.preventDefault(); step(1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, selected, filtersOpen, closeDetail, step]);

  function changeSupplier(value) { setSupplier(value); setOffset(0); }
  function changeSet(value) { setSetCode(value); setOffset(0); }
  function changeYear(value) { setYear(value); if (value && setCode && facets.find(item => item.code === setCode)?.year !== value) setSetCode(''); setOffset(0); }
  function changeFinish(value) { setFinish(value); setOffset(0); }
  function changeSort(value) { setSort(value); setOffset(0); }
  function changeBasis(value) { setPriceBasis(value); if (basisApplies) setOffset(0); }
  function clearPrice() { setMinPrice(''); setMaxPrice(''); setPriceRange({ minPrice: '', maxPrice: '' }); setOffset(0); }
  function clearFilters({ everything = false } = {}) {
    setSetCode(''); setYear(''); setFinish(''); clearPrice();
    if (everything) { setSearch(''); setDebounced(''); setSupplier(''); }
  }
  function goTo(next) {
    setOffset(next);
    if (results.current && results.current.getBoundingClientRect().top < 0) results.current.scrollIntoView({ block: 'start' });
  }

  const chosenSet = facets.find(item => item.code === setCode);
  const chips = [
    setCode && { key: 'set', label: `Set: ${chosenSet ? chosenSet.name : setCode.toUpperCase()}`, clear: () => changeSet('') },
    year && { key: 'year', label: `Released ${year}`, clear: () => changeYear('') },
    finish && { key: 'finish', label: FINISHES.find(item => item.value === finish)?.label, clear: () => changeFinish('') },
    (priceRange.minPrice || priceRange.maxPrice) && {
      key: 'price',
      label: `${priceRange.minPrice && priceRange.maxPrice ? `${money(priceRange.minPrice)} to ${money(priceRange.maxPrice)}` : priceRange.minPrice ? `From ${money(priceRange.minPrice)}` : `Up to ${money(priceRange.maxPrice)}`}${priceBasis === 'latest' ? '' : `, ${basis.label.toLowerCase()}`}`,
      clear: clearPrice,
    },
  ].filter(Boolean);
  const narrowed = Boolean(debounced || supplier || chips.length);
  const sets = useMemo(() => facets.filter(item => item && typeof item.code === 'string' && typeof item.name === 'string'), [facets]);
  const years = useMemo(() => [...new Set(sets.map(item => item.year).filter(Boolean))].sort().reverse(), [sets]);
  const interpreted = yearSearch ? `Showing sets released in ${yearSearch}. Years use known Japanese set release dates.`
    : exactSetSearch ? `Showing the set ${facets.find(item => item.code === exactSetSearch)?.name || debounced}.`
    : year ? 'Years use known Japanese set release dates.'
    : 'Prices are converted from yen. History may be incomplete while older orders import.';

  const cardsView = <div className="sc-cards">
    <div className="sc-toolbar">
      <label className="rc-input sc-search">
        <MagnifyingGlass size={18} aria-hidden="true" />
        <input ref={searchInput} value={search} onChange={event => setSearch(event.target.value)} placeholder="Search card, set, number or year" aria-label="Search card name or number"
          maxLength={120} autoComplete="off" spellCheck={false} enterKeyHint="search" />
        {search ? <button type="button" className="sc-input-clear" aria-label="Clear search" onClick={() => { setSearch(''); searchInput.current?.focus(); }}><X size={16} aria-hidden="true" /></button>
          : <kbd className="sc-search__key" aria-hidden="true">/</kbd>}
      </label>
      <div className="sc-toolbar__row">
        <div className="rc-segmented sc-suppliers" role="group" aria-label="Supplier">
          {SUPPLIERS.map(item => <button key={item.value || 'all'} type="button" aria-pressed={supplier === item.value} onClick={() => changeSupplier(item.value)}>{item.label}</button>)}
        </div>
        <Button className="sc-filters-toggle" icon={<SlidersHorizontal size={18} aria-hidden="true" />} aria-expanded={filtersOpen} aria-controls="sc-filters"
          aria-label={chips.length ? `Filters, ${chips.length} in use` : undefined} onClick={() => setFiltersOpen(open => !open)}>
          Filters{chips.length > 0 && <span className="sc-count" aria-hidden="true">{chips.length}</span>}
        </Button>
      </div>
    </div>

    <div className="sc-filters-scrim" hidden={!filtersOpen} onClick={() => setFiltersOpen(false)} aria-hidden="true" />
    <section id="sc-filters" className="sc-filters" hidden={!filtersOpen} aria-label="Filters">
      <div className="sc-filters__head"><h2>Filters</h2><button type="button" className="rc-btn rc-btn--ghost rc-btn--icon" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><X size={18} aria-hidden="true" /></button></div>
      <div className="sc-filters__body">
        <SetPicker sets={sets} year={year} value={setCode} onChange={changeSet} />
        <label className="rc-field sc-filters__year"><span className="rc-label">Release year</span>
          <span className="rc-input"><select aria-label="Set release year" value={year} onChange={event => changeYear(event.target.value)}>
            <option value="">All years</option>{years.map(value => <option key={value} value={value}>{value}</option>)}
          </select></span>
        </label>
        <fieldset className="sc-filters__price">
          <legend className="rc-label">Price</legend>
          <div className="sc-filters__price-row">
            <div className="rc-segmented" role="group" aria-label="Price based on">
              {BASES.map(item => <button key={item.value} type="button" aria-pressed={priceBasis === item.value} title={item.label} onClick={() => changeBasis(item.value)}>{item.short}</button>)}
            </div>
            <div className="sc-range">
              <label className="rc-input"><span aria-hidden="true">£</span><input inputMode="decimal" aria-label="Minimum price" placeholder="Min" value={minPrice} onChange={event => setMinPrice(event.target.value.trim())} /></label>
              <span className="sc-range__to">to</span>
              <label className="rc-input"><span aria-hidden="true">£</span><input inputMode="decimal" aria-label="Maximum price" placeholder="Max" value={maxPrice} onChange={event => setMaxPrice(event.target.value.trim())} /></label>
            </div>
          </div>
          {priceError && <p className="rc-help rc-help--error" role="alert">{priceError}</p>}
        </fieldset>
        <fieldset className="sc-filters__finish">
          <legend className="rc-label">Finish</legend>
          <div className="sc-chips">{FINISHES.map(item => <button key={item.value || 'any'} type="button" className="rc-chip" aria-pressed={finish === item.value} onClick={() => changeFinish(item.value)}>{item.label}</button>)}</div>
        </fieldset>
      </div>
      <div className="sc-filters__foot">
        <Button variant="ghost" onClick={() => clearFilters()} disabled={!chips.length}>Clear filters</Button>
        <Button variant="primary" className="sc-filters__done" onClick={() => setFiltersOpen(false)}>{loading ? 'Show cards' : `Show ${count(total)} ${total === 1 ? 'card' : 'cards'}`}</Button>
      </div>
    </section>

    {chips.length > 0 && <div className="sc-active" role="group" aria-label="Filters in use">
      {chips.map(chip => <button key={chip.key} type="button" className="rc-chip is-active" onClick={chip.clear} aria-label={`Remove filter ${chip.label}`}>{chip.label}<X size={14} aria-hidden="true" /></button>)}
      <button type="button" className="rc-link sc-active__clear" onClick={() => clearFilters()}>Clear all</button>
    </div>}

    <div className="sc-results" ref={results}>
      <p className="sc-results__count" aria-live="polite">
        {loading && !cards.length && !error ? <Skeleton width={120} height={24} /> : !error && <><strong className="rc-display">{count(total)}</strong> {total === 1 ? 'card' : 'cards'}</>}
      </p>
      <label className="rc-input sc-sort"><span className="rc-label">Sort</span>
        <select aria-label="Sort by" value={sort} onChange={event => changeSort(event.target.value)}>{SORTS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
      </label>
      <p className="sc-results__note">{interpreted}</p>
    </div>

    {error ? <Alert tone="error" title="Cards could not be loaded">{error} <Button size="sm" onClick={() => setReload(value => value + 1)}>Try again</Button></Alert>
      : <div className={`sc-layout${selected ? ' sc-layout--selected' : ''}`}>
        <div className="sc-main">
          {loading && !cards.length ? <TileSkeletons />
            : cards.length === 0 ? <EmptyState icon={MagnifyingGlass} title={narrowed ? 'No cards match' : 'No supplier purchases yet'}
              actions={narrowed ? <Button onClick={() => clearFilters({ everything: true })}>Clear search and filters</Button> : null}>
              {narrowed ? 'Try another name, set or number, or widen the filters.' : 'Cards appear here once Cardrush and Hareruya orders have been imported.'}
            </EmptyState>
            : <div className={`sc-grid${loading ? ' sc-grid--busy' : ''}`} aria-busy={loading || undefined}>
              {cards.map(card => <CardTile key={cardKey(card)} card={card} basis={basis} selected={cardKey(card) === selectedKey} onOpen={item => setSelectedKey(cardKey(item))} />)}
            </div>}
          {total > PAGE_SIZE && <nav className="sc-pagination" aria-label="Pages">
            <Button icon={<CaretLeft size={16} aria-hidden="true" />} disabled={offset === 0 || loading} onClick={() => goTo(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
            <span>{count(offset + 1)}–{count(Math.min(offset + PAGE_SIZE, total))} of {count(total)}</span>
            <Button iconRight={<CaretRight size={16} aria-hidden="true" />} disabled={offset + PAGE_SIZE >= total || loading} onClick={() => goTo(offset + PAGE_SIZE)}>Next</Button>
          </nav>}
        </div>
        {selected && <Detail card={selected} api={api} basis={basis} position={offset + selectedIndex + 1} total={total}
          onPrevious={!loading && offset + selectedIndex > 0 ? () => step(-1) : null} onNext={!loading && offset + selectedIndex + 1 < total ? () => step(1) : null} onClose={closeDetail} />}
      </div>}
  </div>;

  return <Page className="sc-page">
    <PageHeader title="Card costs" description="What we paid for every card bought from Cardrush and Hareruya, converted to pounds." />
    {(messagesApi||api.performance) && <div className="rc-tabs" role="tablist" aria-label="Card costs views">
      <button type="button" role="tab" className="rc-tab" aria-selected={view === 'cards'} onClick={() => setView('cards')}>Cards</button>
      {api.performance&&<button type="button" role="tab" className="rc-tab" aria-selected={view==='performance'} onClick={()=>setView('performance')}>Performance</button>}
      {messagesApi&&<button type="button" role="tab" className="rc-tab" aria-selected={view === 'notices'} onClick={() => setView('notices')}>Order notices &amp; import checks</button>}
    </div>}
    {view==='performance'&&api.performance?<SupplierPerformance api={api}/>:view === 'notices' && messagesApi ? <SupplierOrderMessages api={messagesApi} embedded /> : cardsView}
  </Page>;
}

export default function SupplierOrders() {
  const user = getStoredUser(); const email = String(user?.email || '').trim().toLowerCase();
  const apis = useMemo(() => { const getAccessToken = () => { const current = getStoredUser(); return String(current?.email || '').trim().toLowerCase() === email ? current?.accessToken : null; }; return { api: createSupplierLibraryApi({ getAccessToken }), messagesApi: createSupplierOrdersApi({ getAccessToken }) }; }, [email]);
  if (!canViewSupplierCosts(user)) return <Page><PageHeader title="Card costs" /><Alert tone="error">Sign in with an authorised account to review card costs.</Alert></Page>;
  return <SupplierOrdersReview key={email} api={apis.api} messagesApi={isInventoryOwner(user) ? apis.messagesApi : null} />;
}
